import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/arbiter/badge/redo-badge";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { Tooltip } from "@redotech/arbiter/tooltip/tooltip";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import { memo, type ReactNode } from "react";
import { useCompactSettings } from "./settings-density-context";
import * as css from "./settings-rows.module.css";

export interface SettingsRowBadge {
  text: string;
  color?: RedoBadgeProps["color"];
}

/** Left side of a row: title, description, and optional badge or statuses. */
export const SettingsRowLabel = memo(function SettingsRowLabel({
  title,
  description,
  detail,
  info,
  badge,
  status,
  statusBesideTitle = false,
}: {
  title: string;
  description?: string;
  /** Extra muted line under the description, e.g. a last-event time. */
  detail?: ReactNode;
  /** Longer help shown on an info icon next to the title. */
  info?: string;
  badge?: SettingsRowBadge;
  status?: SettingsRowBadge | SettingsRowBadge[];
  /** Puts statuses right after the title instead of before the description. */
  statusBesideTitle?: boolean;
}) {
  const compact = useCompactSettings();
  const statuses = status ? [status].flat() : [];
  const statusBadges = statuses.map((badge) => (
    <Flex key={badge.text} shrink={0}>
      <RedoBadge color={badge.color ?? "gray"} size="xs" text={badge.text} />
    </Flex>
  ));
  const descriptionText = description && (
    <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
      {description}
    </Text>
  );
  return (
    <Flex className={css.rowLabel} dir="column" gap="xxs">
      <Flex align="center" gap="md">
        <Text
          fontSize={compact ? "xs" : "sm"}
          fontWeight="medium"
          textColor={compact ? "secondary" : "primary"}
        >
          {title}
        </Text>
        {info && (
          <Tooltip arrow leftAlign title={info}>
            <span className={css.infoIcon}>
              <InfoCircleIcon />
            </span>
          </Tooltip>
        )}
        {badge && (
          <Flex shrink={0}>
            <RedoBadge
              color={badge.color ?? "orange"}
              size="xs"
              text={badge.text}
            />
          </Flex>
        )}
        {statusBesideTitle && statusBadges}
      </Flex>
      {statusBesideTitle
        ? descriptionText
        : (statuses.length > 0 || description) && (
            <Flex align="flex-start" gap="md">
              {statusBadges}
              {descriptionText}
            </Flex>
          )}
      {detail && (
        <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
          {detail}
        </Text>
      )}
    </Flex>
  );
});
